import { constant, isArray, isFunction, property, without } from 'underscore';
import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormErrorContext } from './form-error-context';
import { notComp, orComp } from './error-filter-composition';

export const FormErrorGroup = ({ formErrorProps = {}, children }) => {
  const errorsFromProps = formErrorProps.errors;
  const hasErrorsProp = isArray(errorsFromProps);
  const parentContext = useContext(FormErrorContext);
  const [isIsolated, setIsIsolated] = useState(!parentContext || hasErrorsProp);

  const [nodes, setNodes] = useState([]);
  const [nodeUnhandledErrors, setNodeUnhandledErrors] = useState([]);

  useEffect(() => {
    if (parentContext) {
      setIsIsolated(hasErrorsProp);
    }
  }, [parentContext, hasErrorsProp]);

  const addNodeFilter = useCallback((node) => {
    setNodes(currentNodes => [...currentNodes, node]);
  }, []);

  const removeNodeFilter = useCallback((node) => {
    setNodes(currentNodes => without(currentNodes, node));
  }, []);

  const addNodeUnhandledErrors = useCallback((node) => {
    setNodeUnhandledErrors(currentNodes => [...currentNodes, node]);
  }, []);

  const removeNodeUnhandledErrors = useCallback((node) => {
    setNodeUnhandledErrors(currentNodes => without(currentNodes, node));
  }, []);

  const {
    addNodeUnhandledErrors: parentAddNodeUnhandledErrors,
    removeNodeUnhandledErrors: parentRemoveNodeUnhandledErrors,
    errors: parentErrors,
    errorMapper: parentErrorMapper,
    unmappedErrors: parentUnmappedErrors,
    addNodeFilter: parentAddNodeFilter,
    removeNodeFilter: parentRemoveNodeFilter,
    showAllValidationErrors: parentShowAllValidationErrors,
  } = isIsolated ? {} : parentContext;

  const unmappedErrors = useMemo(() => {
    if (!isIsolated) {
      return parentUnmappedErrors;
    }
    const nodePredicates = nodes.map(property('filter')).reduce(orComp, constant(false));
    return errorsFromProps && (typeof errorsFromProps.filter === 'function') && errorsFromProps.filter(notComp(nodePredicates));
  }, [isIsolated, parentUnmappedErrors, errorsFromProps, nodes]);

  const unhandledErrors = useMemo(
    () => nodeUnhandledErrors.map(property('filteredErrors')).reduce((a, b) => a.concat(b), []),
    [nodeUnhandledErrors]
  );

  useEffect(() => {
    let node;
    if (!isIsolated) {
      node = { filteredErrors: unhandledErrors }; // boxing it for by-ref identification
      parentAddNodeUnhandledErrors(node);
    }
    return () => {
      if (node) {
        parentRemoveNodeUnhandledErrors(node);
      }
    };
  }, [isIsolated, unhandledErrors, parentAddNodeUnhandledErrors, parentRemoveNodeUnhandledErrors]);

  const { onUnhandledErrorsChange } = formErrorProps;
  useEffect(() => {
    if (isFunction(onUnhandledErrorsChange)) {
      onUnhandledErrorsChange(unhandledErrors);
    }
  }, [onUnhandledErrorsChange, unhandledErrors]);

  const newContextProps = useMemo(() => (isIsolated ? {
    errors: errorsFromProps,
    errorMapper: formErrorProps.errorMapper,
    unmappedErrors,
    unhandledErrors,
    addNodeFilter,
    removeNodeFilter,
    addNodeUnhandledErrors,
    removeNodeUnhandledErrors,
    showAllValidationErrors: formErrorProps.showAllValidationErrors || parentShowAllValidationErrors,
  } : {
    errors: parentErrors,
    errorMapper: formErrorProps.errorMapper || parentErrorMapper,
    unmappedErrors: parentUnmappedErrors,
    unhandledErrors,
    addNodeFilter: parentAddNodeFilter,
    removeNodeFilter: parentRemoveNodeFilter,
    addNodeUnhandledErrors,
    removeNodeUnhandledErrors,
    showAllValidationErrors: formErrorProps.showAllValidationErrors || parentShowAllValidationErrors,
  }), [isIsolated,
    errorsFromProps,
    formErrorProps.errorMapper,
    parentErrorMapper,
    unmappedErrors,
    unhandledErrors,
    addNodeFilter,
    removeNodeFilter,
    addNodeUnhandledErrors,
    removeNodeUnhandledErrors,
    parentErrors,
    parentUnmappedErrors,
    parentAddNodeFilter,
    parentRemoveNodeFilter,
    formErrorProps.showAllValidationErrors,
    parentShowAllValidationErrors]);

  return (
    <FormErrorContext.Provider value={newContextProps}>
      {children}
    </FormErrorContext.Provider>
  );
};

FormErrorGroup.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  formErrorProps: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.shape({})),
    onUnhandledErrorsChange: PropTypes.func,
    showAllValidationErrors: PropTypes.bool,
  }),
};

FormErrorGroup.defaultProps = {
  formErrorProps: undefined,
};
